import { useEffect, useMemo } from "react";
import { useLocation } from "react-router";
import { styled } from "styled-components";

import { FilterButton, InfinityLoader, ListItemBox, LoadingCards } from "common/components";
import { useDispatch, useSelector } from "common/utilities/react-redux";
import { useTranslate } from "common/utilities/useTranslate";
import { applyKnowledgeDataSearchCriteria } from "domains/knowledgeData/actions";
import { DataItem } from "domains/knowledgeData/components/DataItem";
import {
  createInteractiveDataForTopicSelector,
  getInteractiveDataFilterCount,
  getIsKnowledgeDataFiltered,
  getKnowledgeDataSearchTerm,
} from "domains/knowledgeData/selectors";
import { StyledTitle } from "domains/knowledgeFeed/components/shared";
import { activateModal } from "domains/modal/actions";
import { TopicDetailSection } from "domains/topicDetail/components/TopicDetailSection";
import { TopicSearchBar } from "domains/topicSearchFilter/components/TopicSearchBar";
import { getContentHasFilters } from "domains/topicSearchFilter/selectors";
import { getTmSelectedId, getTmSelectedOuterNode } from "domains/transformationMap/selectors";
import { createKnowledgesSelector } from "entities/knowledge/selectors";
import { getCanAccessTopicData, getCanSeeSectionContent } from "entities/permissions/selectors";
import { createHasUnauthenticatedContentAccessSelector } from "entities/topics/selectors";

const StyledFilterSearchRow = styled.div`
  margin-bottom: 1rem;
  display: flex;
`;

const StyledSearch = styled(TopicSearchBar)`
  margin: 0;
`;

const StyledCard = styled(ListItemBox)`
  padding: 1rem;
`;

const StyledDataItem = styled(DataItem)`
  ${StyledTitle} {
    font-size: 1rem;
    font-weight: 500;
  }
`;

type KnowledgeDataSectionProps = {
  selectedSectionId?: string;
  selectSectionHandle: (id: string) => void;
  topicId: string;
};

const SECTION_ID = "publication-data";

const ITEM_LIMIT = 2;

export const KnowledgeDataSection = ({
  topicId,
  selectedSectionId,
  selectSectionHandle,
}: KnowledgeDataSectionProps) => {
  const isExpanded = SECTION_ID === selectedSectionId;

  const dispatch = useDispatch();
  const location = useLocation();
  const unauthenticatedSectionAccess = useSelector(createHasUnauthenticatedContentAccessSelector(topicId));
  const hasAccess = useSelector(getCanAccessTopicData);
  const canSeeSection = useSelector(getCanSeeSectionContent);
  const hasFilters = useSelector(getContentHasFilters("data"));
  const filterCount = useSelector(getInteractiveDataFilterCount);
  const isFiltered = useSelector(getIsKnowledgeDataFiltered);
  const searchTerm = useSelector(getKnowledgeDataSearchTerm);
  const selectedInnerNodeId = useSelector(getTmSelectedId);
  const selectedOuterNode = useSelector(getTmSelectedOuterNode);
  const translate = useTranslate();

  const id = selectedInnerNodeId ?? topicId;

  const interactiveDataForTopic = useSelector(createInteractiveDataForTopicSelector(id));

  const knowledgesSelector = useMemo(
    () => createKnowledgesSelector(interactiveDataForTopic?.data ?? []),
    [interactiveDataForTopic?.data],
  );

  const knowledge = useSelector(knowledgesSelector).slice(0, isExpanded ? undefined : ITEM_LIMIT);

  const hasNextPage = interactiveDataForTopic?.hasNextPage ?? false;
  const isPending = interactiveDataForTopic?.isFetching ?? false;

  useEffect(() => {
    if (id && knowledge.length === 0) {
      dispatch(applyKnowledgeDataSearchCriteria());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (id && selectedOuterNode?.id) {
      dispatch(applyKnowledgeDataSearchCriteria());
    }
  }, [dispatch, id, selectedOuterNode?.id]);

  const isLocked = !unauthenticatedSectionAccess && hasAccess && !canSeeSection;

  const hide = !canSeeSection && !hasAccess;

  const loadNextPage = () => {
    dispatch(
      applyKnowledgeDataSearchCriteria({
        offset: knowledge.length,
        limit: 20,
      }),
    );
  };

  const onSearchHandle = (search: string) => dispatch(applyKnowledgeDataSearchCriteria({ search }));

  const onSelection = () => selectSectionHandle(SECTION_ID);

  const onFilterClick = () => dispatch(activateModal({ id: "TopicDataFilter", params: { topicId } }));

  const isHidden = selectedSectionId !== SECTION_ID && !!selectedSectionId;
  const path = `${location.pathname}/interactive-data`;

  if (knowledge.length === 0 && !isPending && !isFiltered) {
    return null;
  }

  return (
    <TopicDetailSection
      title={translate("app.discover.topic.tabs.interactive_data.title")}
      display={!hide}
      isExpanded={isExpanded}
      isHidden={isHidden}
      selectSectionHandle={onSelection}
      isLocked={isLocked}
      isPending={isPending}
      filterCount={filterCount}
      hasNextPage={knowledge.length >= ITEM_LIMIT}
      topicId={topicId}
    >
      {isExpanded && (
        <StyledFilterSearchRow>
          <StyledSearch onSearch={onSearchHandle} isPending={isPending} defaultValue={searchTerm} />
          {hasFilters && <FilterButton count={filterCount} onClick={onFilterClick} />}
        </StyledFilterSearchRow>
      )}
      <InfinityLoader loadNextPage={loadNextPage} hasNextPage={isExpanded && hasNextPage} isPending={isPending}>
        {knowledge.map((article) => (
          <ListItemBox key={article.id} data-test-id={article.id}>
            <StyledDataItem
              id={article.id}
              title={article.title}
              interactiveLink={article.interactive_link ?? undefined}
              imageUrl={article.images?.thumbnail}
              source={article.source}
              timestamp={article.time}
              externalLink={article.link}
              detailLink={`${path}/${article.id}`}
            />
          </ListItemBox>
        ))}
        {isPending && knowledge.length === 0 && !isHidden && (
          <LoadingCards
            numberOfItems={ITEM_LIMIT}
            lines={[30, 100]}
            cardHeight={85}
            imageHeight={60}
            imageWidth={100}
            lineMargin={8}
          />
        )}
      </InfinityLoader>
      {knowledge.length === 0 && !isPending && isFiltered && (
        <StyledCard>{translate("app.monitor.empty_message_filter")}</StyledCard>
      )}
    </TopicDetailSection>
  );
};
